import type { SessionSettings } from '@viewer/protocol';
import { useEffect, useRef, useState } from 'react';
import type { ScreenZoom } from './RemoteScreen';
import styles from './SessionSettingsMenu.module.css';

interface Props {
  settings: SessionSettings;
  /** A conexão tem permissão de controle (não é só visualização). */
  canControl: boolean;
  /** 0 a 1. */
  volume: number;
  zoom: ScreenZoom;
  onChange(patch: Partial<SessionSettings>): void;
  onVolumeChange(volume: number): void;
  onZoomChange(zoom: ScreenZoom): void;
}

interface ToggleOption {
  key: 'transmitAudio' | 'blockLocalInput';
  label: string;
  help: string;
  controlOnly?: boolean;
}

const GUIDE_HOLD_CHOICES = [1, 2, 3, 5, 10];

const ZOOM_CHOICES: Array<{ value: ScreenZoom; label: string }> = [
  { value: 'fit', label: 'Ajustar à janela' },
  { value: 'shrink', label: 'Ajustar sem ampliar' },
  { value: 0.5, label: '50%' },
  { value: 0.75, label: '75%' },
  { value: 1, label: '100% (tamanho original)' },
  { value: 1.25, label: '125%' },
  { value: 1.5, label: '150%' },
  { value: 2, label: '200%' },
];

const OPTIONS: ToggleOption[] = [
  {
    key: 'transmitAudio',
    label: 'Transmitir áudio',
    help: 'Recebe o som do computador remoto junto com a tela. Vale só para esta conexão.',
  },
  {
    key: 'blockLocalInput',
    label: 'Bloquear usuário',
    help: 'Bloqueia o mouse e o teclado físicos do computador remoto enquanto você controla. A pessoa de lá vê um aviso no topo da tela.',
    controlOnly: true,
  },
];

/** Configurações da conexão atual; cada sessão tem as suas. */
export function SessionSettingsMenu({
  settings,
  canControl,
  volume,
  zoom,
  onChange,
  onVolumeChange,
  onZoomChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Configurações <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Configurações da conexão">
          <p className={styles.title}>Configurações da conexão</p>
          {OPTIONS.filter((option) => canControl || !option.controlOnly).map((option) => (
            <label key={option.key} className={styles.option}>
              <input
                type="checkbox"
                checked={settings[option.key]}
                onChange={(e) => onChange({ [option.key]: e.target.checked })}
              />
              <span className={styles.text}>
                <span className={styles.label}>{option.label}</span>
                <span className={styles.help}>{option.help}</span>
              </span>
            </label>
          ))}
          <div className={styles.choice}>
            <label htmlFor="session-zoom" className={styles.label}>
              Tamanho da imagem
            </label>
            <select
              id="session-zoom"
              value={String(zoom)}
              onChange={(e) => {
                const choice = ZOOM_CHOICES.find((item) => String(item.value) === e.target.value);
                if (choice) onZoomChange(choice.value);
              }}
            >
              {ZOOM_CHOICES.map((choice) => (
                <option key={choice.value} value={String(choice.value)}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className={styles.help}>
              Muda só a exibição nesta tela: a transmissão continua com a mesma resolução e a proporção é sempre
              mantida. Em 100%, cada pixel do computador remoto ocupa um pixel do seu monitor, sem borrar. Se a
              imagem passar do tamanho da janela, ela acompanha o mouse: leve o ponteiro para a borda e a tela
              rola naquela direção.
            </span>
          </div>
          <div className={styles.choice}>
            <label htmlFor="session-guide-hold" className={styles.label}>
              Devolver o controle após
            </label>
            <select
              id="session-guide-hold"
              value={settings.guideHoldSeconds}
              onChange={(e) => onChange({ guideHoldSeconds: Number(e.target.value) })}
            >
              {GUIDE_HOLD_CHOICES.map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds} s
                </option>
              ))}
            </select>
            <span className={styles.help}>
              Quando a pessoa do outro lado mexe no mouse, ela fica no controle e você só aponta com a seta verde.
              O controle volta para você depois desse tempo sem ela mexer.
            </span>
          </div>
          <div className={`${styles.volume} ${settings.transmitAudio ? '' : styles.volumeDisabled}`}>
            <div className={styles.volumeHead}>
              <label htmlFor="session-volume" className={styles.label}>
                Volume
              </label>
              <span className={styles.volumeValue}>{Math.round(volume * 100)}%</span>
            </div>
            <input
              id="session-volume"
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(volume * 100)}
              disabled={!settings.transmitAudio}
              onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
            />
            {!settings.transmitAudio && <span className={styles.help}>Ligue "Transmitir áudio" para ajustar.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
